import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Icon } from '@neshane/blueberry';
import { useOperationsDashboard } from '../hooks';
import ProviderHealthCard from '../components/ProviderHealthCard';
import { OperationStatusBadge, DlqStatusBadge, CompensationStatusBadge } from '../components/StatusBadges';
import { OPERATION_TYPE_LABELS, PROVIDER_LABELS, formatDateTime } from '../labels';
import { PageEmptyState, Spinner } from '../../../common/components';

function OperationsDashboardContainer() {
    const { dashboard, loading, error, refresh } = useOperationsDashboard();

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center gap-16 py-64 text-secondary-bg-primary">
                <Spinner />
                <p className="body-m">در حال بارگذاری...</p>
            </div>
        );
    }

    if (error) {
        return <p className="body-m text-secondary-alert-default">{error}</p>;
    }

    const summaryCards = [
        {
            icon: 'inbox-stack',
            label: 'عملیات در صف',
            value: dashboard.queue.total,
            variant: 'info',
            to: '/admin/operations/queue',
        },
        {
            icon: 'triangle-warning',
            label: 'صف مرگ (DLQ)',
            value: dashboard.dlq.total,
            variant: 'alert',
            to: '/admin/operations/dlq',
        },
        {
            icon: 'arrow-path',
            label: 'کارهای جبرانی',
            value: dashboard.compensation.total,
            variant: 'warning',
            to: '/admin/operations/compensation',
        },
        {
            icon: 'circle-check',
            label: 'سرویس‌های سالم',
            value: dashboard.health.filter((h) => h.enabled !== false && h.state === 'CLOSED' && h.lastCheckedAt).length,
            variant: 'success',
            to: '/admin/operations/health',
        },
    ];

    const variantBg = {
        info: 'bg-secondary-info-default',
        success: 'bg-secondary-success-default',
        warning: 'bg-secondary-warning-default',
        alert: 'bg-secondary-alert-default',
    };

    return (
        <div className="flex flex-col gap-24">
            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-4">
                    <h1 className="heading-l text-primary">عملیات سیستمی</h1>
                    <p className="body-m text-secondary-bg-primary">
                        نظارت بر سرویس‌های خارجی، صف عملیات، صف مرگ و کارهای جبرانی
                    </p>
                </div>
                <Button text="بروزرسانی" iconName="arrow-path" variant="secondaryNeutral" onClick={refresh} />
            </div>

            <div className="grid grid-cols-4 gap-16">
                {summaryCards.map((stat) => (
                    <Link key={stat.label} to={stat.to} className="no-underline">
                        <Box className="flex items-center gap-16 hover:bg-primary-hover">
                            <span className={`flex items-center justify-center rounded-12 p-12 ${variantBg[stat.variant]}`}>
                                <Icon name={stat.icon} size="l" />
                            </span>
                            <div className="flex flex-col gap-4">
                                <h3 className="title-xl text-primary">{stat.value}</h3>
                                <p className="body-s text-secondary-bg-primary">{stat.label}</p>
                            </div>
                        </Box>
                    </Link>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-16">
                <Box className="flex flex-col gap-16">
                    <h2 className="title-l text-primary">سلامت سرویس‌ها</h2>
                    <div className="grid grid-cols-2 gap-16">
                        {dashboard.health.map((h) => (
                            <ProviderHealthCard key={h.provider} provider={h} />
                        ))}
                    </div>
                </Box>

                <div className="flex flex-col gap-16">
                    <Box className="flex flex-col gap-16">
                        <h2 className="title-l text-primary">آخرین عملیات</h2>
                        {dashboard.queue.recent.length === 0 ? (
                            <PageEmptyState
                                iconName="inbox-stack"
                                iconVariant="info"
                                title="عملیاتی ثبت نشده است"
                                description="هنوز هیچ عملیات خارجی ثبت نشده است"
                            />
                        ) : (
                            <div className="flex flex-col">
                                {dashboard.queue.recent.slice(0, 6).map((op) => (
                                    <div
                                        key={op.id}
                                        className="flex items-center gap-16 border-b border-default py-12 last:border-b-0"
                                    >
                                        <span className="flex items-center justify-center rounded-12 bg-secondary-default p-8">
                                            <Icon name="inbox-stack" size="m" />
                                        </span>
                                        <div className="flex flex-1 flex-col gap-4">
                                            <p className="body-m text-primary">
                                                {OPERATION_TYPE_LABELS[op.operationType] || op.operationType}
                                            </p>
                                            <p className="body-s text-secondary-bg-primary">
                                                {PROVIDER_LABELS[op.provider] || op.provider} • {formatDateTime(op.createdAt)}
                                            </p>
                                        </div>
                                        <OperationStatusBadge status={op.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Box>

                    <Box className="flex flex-col gap-16">
                        <h2 className="title-l text-primary">صف مرگ</h2>
                        {dashboard.dlq.recent.length === 0 ? (
                            <PageEmptyState
                                iconName="circle-check"
                                iconVariant="success"
                                title="صف مرگ خالی است"
                                description="عملیات ناموفقی در انتظار بررسی نیست"
                            />
                        ) : (
                            <div className="flex flex-col">
                                {dashboard.dlq.recent.slice(0, 6).map((entry) => (
                                    <div
                                        key={entry.id}
                                        className="flex items-center gap-16 border-b border-default py-12 last:border-b-0"
                                    >
                                        <span className="flex items-center justify-center rounded-12 bg-secondary-alert-default p-8">
                                            <Icon name="circle-strikeout" size="m" />
                                        </span>
                                        <div className="flex flex-1 flex-col gap-4">
                                            <p className="body-m text-primary">
                                                {OPERATION_TYPE_LABELS[entry.operationType] || entry.operationType}
                                            </p>
                                            <p className="body-s text-secondary-bg-primary">
                                                {PROVIDER_LABELS[entry.provider] || entry.provider} • {entry.lastErrorMessage}
                                            </p>
                                        </div>
                                        <DlqStatusBadge status={entry.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Box>

                    <Box className="flex flex-col gap-16">
                        <h2 className="title-l text-primary">کارهای جبرانی</h2>
                        {dashboard.compensation.recent.length === 0 ? (
                            <PageEmptyState
                                iconName="arrow-path"
                                iconVariant="info"
                                title="کار جبرانی در انتظار نیست"
                                description="همه کارهای جبرانی تکمیل یا بررسی شده‌اند"
                            />
                        ) : (
                            <div className="flex flex-col">
                                {dashboard.compensation.recent.slice(0, 6).map((job) => (
                                    <div
                                        key={job.id}
                                        className="flex items-center gap-16 border-b border-default py-12 last:border-b-0"
                                    >
                                        <span className="flex items-center justify-center rounded-12 bg-secondary-warning-default p-8">
                                            <Icon name="arrow-path" size="m" />
                                        </span>
                                        <div className="flex flex-1 flex-col gap-4">
                                            <p className="body-m text-primary">{job.jobType}</p>
                                            <p className="body-s text-secondary-bg-primary">
                                                مرجع: {job.referenceId} • {formatDateTime(job.createdAt)}
                                            </p>
                                        </div>
                                        <CompensationStatusBadge status={job.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Box>
                </div>
            </div>
        </div>
    );
}

export default OperationsDashboardContainer;